import { AsyncPipe, DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { GalleryDto, GalleryTheme, Section, TenantDto } from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  map,
  mergeWith,
  switchMap,
  takeUntil,
} from 'rxjs';
import { IsDarkColorPipe } from '../../../color-picker/is-dark-color.pipe';
import { MadeWithPicflowButtonComponent } from '../../../gallery-shared/components/made-with-picflow-button/made-with-picflow-button.component';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { singleFilter } from '../../../gallery-shared/lib/filter-assets';
import { loadThemeFonts } from '../../../gallery-shared/lib/load-theme-fonts';
import { ApprovalLimitInfoComponent } from '../../../gallery/components/approval-limit-info/approval-limit-info.component';
import { GalleryComponent } from '../../../gallery/components/gallery/gallery.component';
import { calcLegacyTheme } from '../../../gallery/lib/calc-legacy-theme';
import { GROWTHBOOK } from '../../../growthbook';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { calcGalleryPermissions } from '../../../lib/access-level-helpers';
import { preloadFont } from '../../../lib/preload-font';
import { AssetApprovalLimitService } from '../../../services/asset-approval-limit.service';
import { AssetService } from '../../../services/asset.service';
import { BrowserService } from '../../../services/browser.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { StateService } from '../../../services/state.service';
import { TenantLogoAndContactsComponent } from '../../../shared/components/tenant-logo-and-contacts/tenant-logo-and-contacts.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { PreviewBannerComponent } from '../../components/preview-banner/preview-banner.component';
import { ViewGalleryHeaderComponent } from '../../components/view-gallery-header/view-gallery-header.component';

@Component({
  selector: 'app-view-gallery-page',
  templateUrl: './view-gallery-page.component.html',
  styleUrls: ['./view-gallery-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    MadeWithPicflowButtonComponent,
    AsyncPipe,
    ViewGalleryHeaderComponent,
    TenantLogoAndContactsComponent,
    ApprovalLimitInfoComponent,
    AsyncPipe,
    IsDarkColorPipe,
    PreviewBannerComponent,
    GalleryComponent,
    ButtonDirective,
  ],
})
export class ViewGalleryPageComponent implements OnDestroy {
  public growthBook = inject(GROWTHBOOK);
  public state = inject(StateService);
  private galleryService = inject(GalleryService);
  private browserService = inject(BrowserService);
  private route = inject(ActivatedRoute);
  private assetService = inject(AssetService);
  private sectionService = inject(SectionService);
  private platformId = inject(PLATFORM_ID);
  private assetApprovalLimitService = inject(AssetApprovalLimitService);
  private permissionService = inject(PermissionService);
  private document = inject(DOCUMENT);

  isBrowser = isPlatformBrowser(this.platformId);
  gallery = toSignal(
    this.route.parent.data.pipe(
      mergeWith(this.route.data),
      filter(data => data.gallery),
      switchMap(data => this.galleryService.getGallery(data.gallery.id))
    )
  );
  theme = computed(() => {
    if (!this.gallery()) return;
    return this.gallery().theme || calcLegacyTheme(this.gallery());
  });
  gallery$ = toObservable(this.gallery);
  tenant = computed(() => this.gallery()?.tenant as TenantDto);
  correctPicflowUrl = this.browserService.getCorrectPicflowUrl();
  onFilterChange$ = new BehaviorSubject<void>(undefined);
  sections$: Observable<Section[]> = this.gallery$.pipe(
    filter(() => isPlatformBrowser(this.platformId)),
    filter(Boolean),
    map(gallery => gallery.id),
    distinctUntilChanged(),
    switchMap(galleryId => this.sectionService.getSections(galleryId))
  );

  private noAssetsResults$: Observable<boolean> = this.gallery$.pipe(
    filter(Boolean),
    map(gallery => gallery.id),
    distinctUntilChanged(),
    mergeWith(
      this.onFilterChange$.pipe(
        switchMap(() =>
          this.gallery$.pipe(
            filter(Boolean),
            map(gallery => gallery.id)
          )
        )
      )
    ),
    filter(() => isPlatformBrowser(this.platformId)),
    switchMap(galleryId => this.assetService.getAssetsForGalleryObservable(galleryId)),
    map(assets => {
      if (!this.filters().length) return false;
      const filteredAssets = assets.filter(asset => !this.filters().some(filter => singleFilter(filter, asset)));
      return filteredAssets.length === 0;
    })
  );

  platformBrowser = isPlatformBrowser(this.platformId);
  parentGallery: GalleryDto;
  private accessLevel = toSignal(
    this.route.parent.data.pipe(
      mergeWith(this.route.data),
      filter(d => d.accessLevel),
      map(data => data.accessLevel as GalleryAccessLevel)
    )
  );
  public galleryPermissions = computed(() => calcGalleryPermissions(this.gallery(), this.accessLevel()));

  filters = signal<Filter[]>([]);
  noAssetsResult = toSignal(this.noAssetsResults$);
  destroyed$ = new Subject<void>();
  approvedAssetCount$ = toObservable(this.gallery).pipe(
    switchMap(gallery => {
      return this.assetApprovalLimitService.getApprovedAssetCountForGallery(gallery.id);
    })
  );
  allowedAndEnabledFeedbackOptions = computed(() => {
    return this.permissionService.getAllowedAndEnabledFeedbackOptions(
      this.gallery(),
      this.galleryPermissions()?.can_review_if_authenticated
    );
  });
  @ViewChild('header') header: ViewGalleryHeaderComponent;

  constructor() {
    effect(() => {
      const gallery = this.gallery();
      const theme = this.theme();
      const tenant = this.tenant();
      if (!gallery || !tenant) return;
      if (isPlatformServer(this.platformId)) {
        this.preloadFonts(theme);
      } else {
        loadThemeFonts(theme, tenant);
      }
    });

    effect(() => {
      const galleryPermissions = this.galleryPermissions();
      const gallery = this.gallery();
      if (!galleryPermissions || !gallery) return;
      if (galleryPermissions.can_edit && !this.state.galleryPreviewMode) {
        location.href = this.correctPicflowUrl + '/a/gallery/' + gallery.id + '?fromPublicLink=true';
      }
    });

    this.galleryService.onGalleryDeleted$.pipe(takeUntil(this.destroyed$)).subscribe(async deletedGallery => {
      const gallery = this.gallery();
      if (!deletedGallery || !gallery) return;
      if (deletedGallery.id !== gallery.id) return;
      this.browserService.reload(true);
    });
  }

  async onFilter(filters: Filter[]) {
    this.filters.set(filters);
    this.onFilterChange$.next();
  }

  resetFilter() {
    this.header?.viewFilter?.resetFilter();
  }

  niceSite(site: string) {
    if (!site) return '';
    if (site.split('//').length === 1) return site;
    return site.split('//')[1];
  }

  preloadFonts(theme: GalleryTheme) {
    const fonts = [
      theme.cover.title_typography?.font_family,
      theme.cover.paragraph_typography?.font_family,
      theme.section.title_typography?.font_family,
      theme.section.paragraph_typography?.font_family,
    ].filter(Boolean);

    fonts.forEach(font => {
      preloadFont(font, this.document);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
